import { ApiError } from '../../../shared/api/ApiClient.js';
import { CUSTOMER_OUTBOUND_QUEUE_OVERFLOW_CODE } from '../../../shared/http/customerOutboundFairQueue.js';
import { HTTP_STORM_LATCHED_CODE } from '../../../shared/http/stormAwareFetch.js';
import { isMissingCredentialUnauthorized } from '../../../shared/session/fetchCustomerMeCredentials.js';

/**
 * Localized copy keys for unified Objednávky list load failures.
 * Auth 401/403 → session-expired; storm/overflow 429 → rate-limit retry copy;
 * otherwise connection copy.
 * Mirrors `resolveReceiptDownloadUserMessageKey` HttpError mapping.
 */
export type UnifiedOrdersLoadMessageKey =
  | 'account.orders.loadError'
  | 'account.orders.loadSessionExpired'
  | 'account.orders.loadRateLimited';

export type AccountPaymentsLoadMessageKey =
  | 'account.payments.loadError'
  | 'account.payments.loadSessionExpired'
  | 'account.payments.loadRateLimited';

const ACCOUNT_RATE_LIMIT_ERROR_CODES = new Set<string>([
  'HTTP_STORM_DETECTED',
  CUSTOMER_OUTBOUND_QUEUE_OVERFLOW_CODE,
  HTTP_STORM_LATCHED_CODE,
]);

export function isAccountRateLimitFailure(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.status !== 429) {
      return false;
    }
    return error.code !== undefined && ACCOUNT_RATE_LIMIT_ERROR_CODES.has(error.code);
  }
  if (error !== null && typeof error === 'object') {
    const status = 'status' in error ? (error as { status?: unknown }).status : undefined;
    const code = 'code' in error ? (error as { code?: unknown }).code : undefined;
    return (
      status === 429 &&
      typeof code === 'string' &&
      ACCOUNT_RATE_LIMIT_ERROR_CODES.has(code)
    );
  }
  return false;
}

export function isUnifiedOrdersAuthFailure(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      return true;
    }
    return isMissingCredentialUnauthorized(error.code, error.message);
  }
  if (error !== null && typeof error === 'object') {
    const status = 'status' in error ? (error as { status?: unknown }).status : undefined;
    const code = 'code' in error ? (error as { code?: unknown }).code : undefined;
    const message =
      'message' in error ? (error as { message?: unknown }).message : undefined;
    if (status === 401 || status === 403) {
      return true;
    }
    return isMissingCredentialUnauthorized(
      typeof code === 'string' ? code : undefined,
      typeof message === 'string' ? message : undefined,
    );
  }
  return false;
}

/** Shared auth-failure detector for account list screens (orders + payments). */
export const isAccountAuthFailure = isUnifiedOrdersAuthFailure;

/** Prefer session-expired when any contributing query failed with 401/403. */
export function resolveUnifiedOrdersLoadMessageKey(
  ...errors: unknown[]
): UnifiedOrdersLoadMessageKey {
  if (errors.some(isUnifiedOrdersAuthFailure)) {
    return 'account.orders.loadSessionExpired';
  }
  if (errors.some(isAccountRateLimitFailure)) {
    return 'account.orders.loadRateLimited';
  }
  return 'account.orders.loadError';
}

/** Prefer session-expired when any payments query failed with 401/403. */
export function resolveAccountPaymentsLoadMessageKey(
  ...errors: unknown[]
): AccountPaymentsLoadMessageKey {
  if (errors.some(isAccountAuthFailure)) {
    return 'account.payments.loadSessionExpired';
  }
  if (errors.some(isAccountRateLimitFailure)) {
    return 'account.payments.loadRateLimited';
  }
  return 'account.payments.loadError';
}
